import {Link} from "react-router-dom";
import {css, Theme} from "@emotion/react";
import {elevation} from "@pg-design/elevation";
import {onDesktop, w100} from "@pg-design/helpers-css";
import {Picture} from "@pg-design/picture";
import {getTextVariantStyleFor, Text} from "@pg-design/text";
import {compile} from "path-to-regexp";

import {formatDate, shortDate} from "../../../../../utils/dates";
import {chartsRoutes} from "../../../common/app/routing/charts_routes";
import {truncateMultiline} from "../../../components/css_helpers/truncate";
import {EntryType, IEntry} from "../../newshub/types/Entry";
import {resolveNewshubMediaUrl} from "../../newshub/utils/resolve_newshub_media_url";

import ghArticleMock from "../../newshub/assets/bd_gh_article_mock.png";
import ghVideoArticleMock from "../../newshub/assets/bd_gh_video_article_mock.png";
import pressReviewArticleMock from "../../newshub/assets/bd_press_review_article_mock.png";
import rpArticleMock from "../../newshub/assets/bd_rp_article_mock.png";
import rpVideoArticleMock from "../../newshub/assets/bd_rp_video_article_mock.png";

const HIGHLIGHTED_TYPES = {
    RP: 1,
    GH: 2
};

const getRoute = (type: EntryType, language: string) => {
    const routes = chartsRoutes[language];

    if (type === EntryType.ARTICLE) {
        return routes.newshub.marketInformation.details;
    }
    if (type === EntryType.BIG_DATA_NEWS) {
        return routes.newshub.bigDataNews.details;
    }
    if (type === EntryType.PRESS_INFORMATION) {
        return routes.pressOffice.pressInformation.details;
    }
    if (type === EntryType.PRESS_REVIEW) {
        return routes.pressOffice.pressReview.details;
    }
    throw new Error("Unknown entry type");
};

interface ICardImages {
    mobile: string;
    desktop: string;
}

const sameForBothSlots = (src: string): ICardImages => ({mobile: src, desktop: src});

/**
 * Okładki są w 1.6, a sloty karty mają 1.25 na mobile i 0.914 na desktopie, więc kadr musi zostać przycięty.
 * Bierzemy renditiony już w proporcji slotu – wtedy przycina Wagtail (wokół focal pointa, jeśli redakcja go
 * ustawi), a nie CSS, co daje ostrzejszy obraz i sensowniejszy kadr niż wymuszanie tego po naszej stronie.
 */
const getArticleImages = (article: IEntry): ICardImages | null => {
    if (article.is_highlighted) {
        if (article.highlighted_type === HIGHLIGHTED_TYPES.RP) {
            return sameForBothSlots(article.is_video_material ? rpVideoArticleMock : rpArticleMock);
        }
        if (article.highlighted_type === HIGHLIGHTED_TYPES.GH) {
            return sameForBothSlots(article.is_video_material ? ghVideoArticleMock : ghArticleMock);
        }
        return null;
    }

    const {img_320x256, img_426x466, img_332x208} = article.images ?? {};
    const mobile = img_320x256 || img_332x208;
    const desktop = img_426x466 || img_332x208;

    if (mobile && desktop) {
        return {mobile: resolveNewshubMediaUrl(mobile), desktop: resolveNewshubMediaUrl(desktop)};
    }

    if (article.type === EntryType.PRESS_REVIEW) {
        return sameForBothSlots(pressReviewArticleMock);
    }

    return null;
};

interface ILandingMarketNewsCardProps {
    article: IEntry;
    language: string;
}

export const LandingMarketNewsCard = ({article, language}: ILandingMarketNewsCardProps) => {
    const route = getRoute(article.type, language);
    const path = compile(route)({newsId: String(article.id), slug: article.slug});
    const articleImages = getArticleImages(article);

    return (
        <div css={cardRoot}>
            <Link to={path} css={cardLink}>
                <article css={card}>
                    {articleImages && (
                        <div css={pictureWrapper}>
                            <Picture
                                css={picture}
                                sources={[
                                    {minWidthPX: 0, src: articleImages.mobile, width: 160, height: 128},
                                    {minWidthPX: 1024, src: articleImages.desktop, width: 213, height: 233}
                                ]}
                                alt={article.title}
                                loading="lazy"
                            />
                        </div>
                    )}

                    <div css={content}>
                        <Text css={metaText} as="div" variant="info_txt_2">
                            <span css={date}>{formatDate(article.publication_date, shortDate)}</span>
                            {article.author && <span css={author}>{article.author}</span>}
                        </Text>

                        <Text css={articleTitle} as="h6">
                            {article.title}
                        </Text>

                        {article.lead && (
                            <Text css={articleLead} variant="body_copy_1">
                                {article.lead}
                            </Text>
                        )}
                    </div>
                </article>
            </Link>
        </div>
    );
};

const cardRoot = css`
    min-width: 0;
    ${w100};
    height: 100%;
`;

const cardLink = css`
    display: flex;
    ${w100};
    height: 100%;
    text-decoration: none;
    color: inherit;
`;

const card = css`
    display: flex;
    flex-direction: row;
    align-items: stretch;
    overflow: hidden;
    ${w100};
    height: 100%;
    min-height: 128px;
    border-radius: 16px;
    background-color: #fff;
    ${elevation(1)};
    transition: box-shadow 0.2s ease-in-out;

    &:hover {
        ${elevation(4)};
    }

    ${onDesktop(css`
        min-height: 233px;
    `)};
`;

const pictureWrapper = css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 160px;
    height: 128px;
    overflow: hidden;

    ${onDesktop(css`
        width: 213px;
        height: 233px;
        flex-shrink: 0;
    `)};
`;

const picture = css`
    width: 100%;
    height: 100%;

    img {
        display: block;
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const content = css`
    display: flex;
    flex: 1 0 0;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    min-height: 128px;
    min-width: 0;
    padding: 8px;

    ${onDesktop(css`
        width: 208px;
        flex-shrink: 0;
        align-self: stretch;
        gap: 12px;
        height: auto;
        min-height: 233px;
        padding: 16px;
    `)};
`;

const metaText = css`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    min-width: 0;
`;

const date = css`
    flex-shrink: 0;
`;

const author = css`
    flex-shrink: 1;
    min-width: 0;
    overflow: hidden;
    text-align: right;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const articleTitle = (theme: Theme) => css`
    ${getTextVariantStyleFor("body_copy_1")(theme)};
    ${w100};
    flex: 0 1 auto;
    min-height: 0;
    margin: 0;
    ${truncateMultiline(3)};

    ${onDesktop(css`
        ${getTextVariantStyleFor("headline_6")(theme)};
        ${truncateMultiline(2)};
    `)};
`;

const articleLead = css`
    ${w100};
    flex: 1 1 auto;
    min-height: 0;
    margin: 0;
    display: none;

    ${onDesktop(css`
        display: block;
        ${truncateMultiline(3)};
    `)};
`;
